/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

import Loading from 'components/Loading/Loading';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import AddNewSiteForm from './AddNewSiteForm';
import AddNewSiteSuccess from './AddNewSiteSuccess';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  formData: Object,
  actionDrawerStore: TypeActionDrawerStore,
  config: Object,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore', 'accountStore')
@observer
export default class AddNewSite extends React.Component<*> {
  createForm = () => {
    const { t, accountStore }: TranslatorProps = this.props;
    const { actionDrawerStore, config }: TypeProps = this.props;
    const fields: Array<Object> = [
      {
        name: 'addSiteForm',
        label: t('general:actions.new_site'),
        title: 'New Site',
        fields: [
          {
            name: 'tradingAccount',
            label: t('general:trading_account'),
            rules: 'required|string',
            disabled: true,
            value: this.props.params.id,
          },
          {
            name: 'company',
            label: t('general:company'),
            rules: 'required|string',
            disabled: true,
            value: config.companyId,
          },
          {
            name: 'siteName',
            label: t('general:locations.labels.site'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'streetNo',
            label: t('general:labels.street_number'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'townCity',
            label: t('general:town_city'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'stateCountryProvince',
            label: t('general:state_county_province'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'zipCode',
            label: t('general:zip_postcode'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'country',
            label: t('general:country'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'contactPhoneNo',
            label: t('general:labels.contact_phone_number'),
            rules: 'telephone',
            value: '',
          },
          {
            name: 'contactFaxNo',
            label: t('general:labels.contact_fax_number'),
            rules: 'telephone',
            value: '',
          },
          {
            name: 'locationContact',
            label: t('general:locations.labels.contact'),
            rules: 'string',
            value: '',
          },
          {
            name: 'buildingtransport',
            label: t('general:locations.labels.building_transport'),
            rules: 'string',
            value: '',
          },
          {
            name: 'roomLab',
            label: t('general:locations.labels.lab_room'),
            rules: 'string',
            value: '',
          },
          {
            name: 'systemWorkspace',
            label: t('general:locations.labels.system_workspace'),
            rules: 'string',
            value: '',
          },
          {
            name: 'systemWorkspaceIsOwned',
            label: t('general:locations.messages.system_workspace_is_owned'),
            rules: '',
            value: '',
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const {
          company,
          siteName,
          streetNo,
          townCity,
          stateCountryProvince,
          zipCode,
          country,
          contactPhoneNo,
          contactFaxNo,
          locationContact,
        }: Object = data.values().addSiteForm;

        const isAssetRequest = accountStore.locationSelections[0].asset_request_location;
        const locationType = accountStore.locationSelections[0].asset_request_location_type;

        const jsonObj: Object = {
          company_id: company,
          name: siteName,
          country_id: country,
          contact: locationContact,
          contact_phone: contactPhoneNo,
          contact_fax: contactFaxNo,
          address: {
            postcode: zipCode,
            address_1: streetNo,
            address_2: townCity,
            address_4: stateCountryProvince,
          },
        };

        !locationContact && delete jsonObj.contact;
        !contactPhoneNo && delete jsonObj.contact_phone;
        !contactFaxNo && delete jsonObj.contact_fax;

        actionDrawerStore.userAddLocation(jsonObj, isAssetRequest, locationType);
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: t('general:actions.new_site'),
      }
    );
  };

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.create_new_site')} />
          <div className={styles['action-drawer-content']}>
            {actionDrawerStore.errorResponse.message}
          </div>
          <div className={styles['action-drawer-actions']}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => actionDrawerStore.closeActionDrawer()}
            >
              {t('general:actions.close')}
            </Button>
          </div>
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return <AddNewSiteSuccess config={this.props.config} />;
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.create_new_site')} />
        <AddNewSiteForm form={this.createForm()} config={this.props.config} />
      </div>
    );
  }
}
